// autopilot-doctor.ts
//
// `autopilot doctor [--json]` (spec §3.3/§18, N36, tests 41-43). Reuses
// session-preflight-checks.ts (P2) DIRECTLY for settings/hook/helper checks —
// no reimplementation (test 43). Adds lock/journal/pack-pin/compatibility
// checks. Blocks `run` when journal status is RECOVERING (test 42). No
// Mattermost bot REST check here — that's P7's job.

import {
  type AutopilotCheckResult,
  checkCompatibilityDigests,
  checkJournalStatus,
  checkPackPin,
} from "./autopilot-doctor-manifest-checks.js";
import {
  checkMattermostBotPermissionForDoctor,
  type MattermostDoctorCheckDeps,
} from "./autopilot-doctor-mattermost-check.js";
import { type RunOpsDoctorChecksInput, runOpsDoctorChecks } from "./autopilot-doctor-ops-checks.js";
import { probeBinaryVersion } from "./claude-version-probe.js";
import { checkDangerousCommandGuardOptInForDoctor } from "./dangerous-command-guard-opt-in.js";
import {
  checkBindingHelperInPack,
  checkHookCommandsExist,
  checkMissionSettingsParse,
} from "./session-preflight-checks.js";

export type { AutopilotCheckResult };

export interface AutopilotDoctorInput {
  missionWorkspace: string;
  packRoot: string;
  controlRoot: string;
  cliVersion?: string;
  /** P7 test 53: optional — omit when Mattermost isn't configured for this mission yet. */
  mattermostCheckDeps?: MattermostDoctorCheckDeps;
  /** P8 Agent H: optional — omit to skip pin-drift/free-disk/retention checks (e.g. pre-init doctor calls with no effective-config loaded yet). */
  opsChecks?: Omit<RunOpsDoctorChecksInput, "missionWorkspace" | "controlRoot">;
  /** Seam for the hook-interpreter check — default is the real `python3 --version` spawn (claude-version-probe.ts); tests inject a fake. */
  probeBinaryVersion?: (binary: "python3") => Promise<string>;
}

export interface AutopilotDoctorResult {
  checks: AutopilotCheckResult[];
  blocksRun: boolean;
}

export async function runAutopilotDoctorChecks(
  input: AutopilotDoctorInput,
): Promise<AutopilotDoctorResult> {
  const checks: AutopilotCheckResult[] = [];

  // Reuse P2 preflight checks DIRECTLY — no reimplementation (test 43).
  const settingsParse = await checkMissionSettingsParse(input.missionWorkspace);
  checks.push({
    name: "Mission settings parse",
    status: settingsParse.ok ? "ok" : "fail",
    detail: settingsParse.ok
      ? "settings.json hợp lệ"
      : `${settingsParse.reason}: ${settingsParse.details ?? ""}`,
  });

  const hookCheck = await checkHookCommandsExist(input.missionWorkspace);
  checks.push({
    name: "Hook commands exist",
    status: hookCheck.ok ? "ok" : "fail",
    detail: hookCheck.ok
      ? "mọi hook command đều tồn tại"
      : `${hookCheck.reason}: ${hookCheck.details ?? ""}`,
  });

  // D-03 follow-up (2026-09-27): "Hook commands exist" only proves the hook
  // SCRIPTS are on disk. Every mission hook is spawned as `python3 -B …`
  // (mission-workspace-hooks.ts); without the interpreter each hook exits 127,
  // which Claude Code treats as a NON-blocking hook error — the guards would
  // silently never run. This list feeds startSupervisor's `doctorBlocksRun`
  // (the doctor COMMAND's environment group does not), so the check lives
  // here and blocks `run`. CI image node:22 measured 2026-09-27: Python 3.11.2.
  const python3Version = await (input.probeBinaryVersion ?? probeBinaryVersion)("python3");
  const python3Missing = python3Version === "(unavailable)";
  checks.push({
    name: "Hook interpreter (python3)",
    status: python3Missing ? "fail" : "ok",
    detail: python3Missing
      ? "không gọi được 'python3 --version' — mọi hook pack (python3 -B …) sẽ thoát 127 và guard không chạy; cài python3 và đưa vào PATH"
      : python3Version,
  });

  const helperCheck = await checkBindingHelperInPack(input.packRoot);
  checks.push({
    name: "Binding helper in pack",
    status: helperCheck.ok ? "ok" : "fail",
    detail: helperCheck.ok
      ? "autopilot_binding_common.py có mặt"
      : `${helperCheck.reason}: ${helperCheck.details ?? ""}`,
  });

  // Compatibility manifest / digest / pack pin.
  checks.push(await checkCompatibilityDigests(input.controlRoot, input.missionWorkspace));
  checks.push(await checkPackPin(input.controlRoot, input.missionWorkspace));
  checks.push(await checkDangerousCommandGuardOptInForDoctor(input.missionWorkspace));

  // Journal status — blocks `run` when RECOVERING (test 42).
  const { check: journalCheck, blocksRun } = await checkJournalStatus(input.controlRoot);
  checks.push(journalCheck);

  // Mattermost bot REST permission (P7 test 53) — opt-in via deps so a
  // mission without Mattermost configured yet doesn't fail doctor on it.
  if (input.mattermostCheckDeps) {
    checks.push(await checkMattermostBotPermissionForDoctor(input.mattermostCheckDeps));
  }

  // P8 Agent H: pin drift / free disk / retention — opt-in via deps for the
  // same reason (pre-init doctor calls have no effective-config yet).
  let opsBlocksRun = false;
  if (input.opsChecks) {
    const ops = await runOpsDoctorChecks({
      missionWorkspace: input.missionWorkspace,
      controlRoot: input.controlRoot,
      ...input.opsChecks,
    });
    checks.push(...ops.checks);
    opsBlocksRun = ops.blocksRun;
  }

  return { checks, blocksRun: blocksRun || opsBlocksRun || python3Missing };
}

export { checkMattermostBotPermissionForDoctor, type MattermostDoctorCheckDeps };

/** Mirrors doctor.ts's buildDoctorJsonPayload exactly — pure, no I/O. */
export function buildAutopilotDoctorJsonPayload(result: AutopilotDoctorResult): {
  checks: AutopilotCheckResult[];
  blocks_run: boolean;
  summary: { total: number; passed: number; issues: number };
} {
  const passed = result.checks.filter((c) => c.status === "ok").length;
  return {
    checks: result.checks,
    blocks_run: result.blocksRun,
    summary: { total: result.checks.length, passed, issues: result.checks.length - passed },
  };
}
